/* -------------------------------------------------------------------------- */
/*                    Componente SlidingPaneAddEditCustomer                   */
/* -------------------------------------------------------------------------- */
// Este componente permite crear o editar clientes

import * as authSelectors from '@redux/reducers/auth';
import omit from 'lodash/omit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Badge, Button, Col, Label, Row } from 'reactstrap';
import { X } from 'react-feather';
import { v4 as uuidv4 } from 'uuid';
import Membership from 'views/formComponents/Membership';
import * as customerActions from 'views/screens/Customers/actions';
import * as customerSelectors from 'views/screens/Customers/reducers';
import * as locationActions from 'views/screens/Locations/actions';
import * as locationSelectors from 'views/screens/Locations/reducers';
import SlidingPaneSelectBranchesToLink from 'views/screens/Customers/views/SlidingPaneSelectBranchesToLink';
import SlidingPaneEditLocation from 'views/screens/Locations/views/SlidingPaneEditLocation';
import TextInput from '../../formComponents/TextInput';
import LocationsDropdownInput from '../LocationDropDown';

interface Props {
  customer: any;
  isOpenSlidingPane: any;
  closeSlidingPaneAddEditCustomer: any;
  enableSelectLocation?: boolean;
}

const SlidingPaneAddEditCustomer = (props: Props) => {
  const {
    customer,
    isOpenSlidingPane,
    closeSlidingPaneAddEditCustomer,
    enableSelectLocation = false
  } = props;
  const { control, handleSubmit, watch, reset, setValue } = useForm();
  const intl = useIntl();
  const dispatch = useDispatch();
  const initializedCustomerIdRef = useRef<string | null>(null);
  const [isBranchesPickerOpen, setIsBranchesPickerOpen] = useState(false);
  const [isCreateBranchOpen, setIsCreateBranchOpen] = useState(false);
  const wasAddingLocationRef = useRef(false);
  const locationIdsBeforeAddRef = useRef<Set<number>>(new Set());

  const formValues = watch();

  const { isNew, initialValues, parsers, allLocations, isAddingLocation } = useSelector(
    (state) => {
      const initialValues = customerSelectors.getSelectedCustomer(state);
      return {
        isNew: initialValues ? false : true,
        initialValues,
        parsers: authSelectors.getParsers(state),
        allLocations: locationSelectors.getLocationsList(state),
        isAddingLocation: locationSelectors.isAddingLocation(state)
      };
    }
  );

  // Detectar cuando se termina de agregar una location y agregarla a branches
  useEffect(() => {
    if (wasAddingLocationRef.current && !isAddingLocation) {
      const idsBefore = locationIdsBeforeAddRef.current;
      const newIds = allLocations
        .map((l) => l.locationId)
        .filter((id) => !idsBefore.has(id));
      if (newIds.length > 0) {
        const current: number[] = (watch('branches') as any) ?? [];
        const merged = Array.from(new Set([...current, ...newIds]));
        setValue('branches', merged, { shouldDirty: true });
      }
    }
    wasAddingLocationRef.current = isAddingLocation;
  }, [isAddingLocation, allLocations]);

  const customerId = initialValues?.customerId;

  const initialBranchIds = useMemo(() => {
    if (!customerId) return [];
    return allLocations
      .filter((loc) => loc.customerId === customerId && loc.locationClassId === 34)
      .map((loc) => loc.locationId);
  }, [allLocations, customerId]);

  const selectedBranchIds: number[] = formValues?.branches ?? [];

  const mainOfficeOptions = useMemo(() => {
    return allLocations
      .filter((loc) => selectedBranchIds.includes(loc.locationId))
      .map((loc) => ({
        ...loc,
        id: loc.locationId,
        value: loc.locationId,
        label: loc.locationName,
        label_intl: loc.locationName
      }));
  }, [allLocations, selectedBranchIds]);

  const linkBranches = (newBranches: number[], targetCustomerId: any) => {
    newBranches.forEach((locationId) => {
      const location = allLocations.find((l) => l.locationId === locationId);
      if (location && location.customerId !== targetCustomerId) {
        dispatch(
          locationActions.startEditingLocation({
            ...location,
            customerId: targetCustomerId,
            locationTypeId: 1,
            locationClassId: 34,
            __silent: true
          })
        );
      }
    });
  };

  const unlinkBranches = (removedBranches: number[]) => {
    removedBranches.forEach((locationId) => {
      const location = allLocations.find((l) => l.locationId === locationId);
      if (location) {
        dispatch(
          locationActions.startEditingLocation({
            ...location,
            customerId: null,
            locationTypeId: 2,
            locationClassId: 0,
            __silent: true
          })
        );
      }
    });
  };

  const editCustomer = (values) => {
    const newBranches: number[] = values.branches ?? [];
    dispatch(customerActions.startEditingCustomer(getValues(customerId, values)));
    linkBranches(newBranches, customerId);
    unlinkBranches(initialBranchIds.filter((id) => !newBranches.includes(id)));
    closeSlidingPaneAddEditCustomer();
    reset();
  };

  const createCustomer = (values) => {
    const tempId = uuidv4();
    const newBranches: number[] = values.branches ?? [];
    dispatch(
      customerActions.startAddingCustomer({
        ...getValues(tempId, values),
        branchesToLink: newBranches
      })
    );
    closeSlidingPaneAddEditCustomer();
    reset();
  };

  const getValues = (customerId, values) => ({
    ...omit(values, ['membership', 'office', 'officeId', 'branches']),
    customerId,
    organizationId: values.membership?.organizationId,
    divisionId: values.membership?.divisionId,
    subdivisionId: values.membership?.subdivisionId,
    mainOfficeId: values.mainOfficeId
  });

  useEffect(() => {
    if (customer?.customerId) {
      dispatch(customerActions.selectCustomer(customer?.customerId));
      return () => {
        dispatch(customerActions.deselectCustomer(customer?.customerId));
      };
    }
  }, [customer?.customerId]);

  // Resetear el formulario cuando initialValues cambie
  useEffect(() => {
    // Solo resetear si el customerId cambió o si estamos abriendo el panel
    if (isOpenSlidingPane && initialValues && customer?.customerId && customer?.customerId !== initializedCustomerIdRef.current) {
      initializedCustomerIdRef.current = customer?.customerId || null;
      reset({
        customerName: initialValues?.customerName,
        distributionChannel: initialValues?.distributionChannel,
        businessName: initialValues?.businessName,
        taxId: initialValues?.taxId,
        privateCode: initialValues?.privateCode,
        customerAddress: initialValues?.customerAddress,
        customerEmails: initialValues?.customerEmails,
        contactName1: initialValues?.contactName1,
        contactName2: initialValues?.contactName2,
        contactName3: initialValues?.contactName3,
        phone1: initialValues?.phone1,
        phone2: initialValues?.phone2,
        phone3: initialValues?.phone3,
        comments: initialValues?.comments,
        mainOfficeId: initialValues?.mainOfficeId,
        branches: initialBranchIds,
        membership: {
          organizationId: initialValues?.organizationId,
          divisionId: initialValues?.divisionId,
          subdivisionId: initialValues?.subdivisionId
        }
      });
    } else if (isOpenSlidingPane && !customer?.customerId && initializedCustomerIdRef.current !== 'new') {
      // Resetear a valores vacíos cuando es nuevo cliente
      initializedCustomerIdRef.current = 'new';
      reset({});
    } else if (!isOpenSlidingPane) {
      // Limpiar la referencia cuando se cierra el panel
      initializedCustomerIdRef.current = null;
    }
  }, [isOpenSlidingPane, initialValues, customer?.customerId]);

  return (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName="custom-overlay-sliding-pane"
      isOpen={isOpenSlidingPane}
      title={intl.formatMessage({ id: isNew ? 'customers.create' : 'customers.edit' })}
      subtitle={isNew ? undefined : initialValues?.customerName}
      width={'45rem'}
      onRequestClose={() => {
        closeSlidingPaneAddEditCustomer();
        reset();
      }}
    >
      <Row>
        <Membership
          name="membership"
          control={control}
          defaultValue={{
            organizationId: initialValues?.organizationId,
            divisionId: initialValues?.divisionId,
            subdivisionId: initialValues?.subdivisionId
          }}
          options={{
            useOperator: false,
            useUserOrganization: true,
            showAllDivision: true,
            showAllSubdivision: true,
            colSize: 6
          }}
        />
        <Col sm="12">
          <Controller
            control={control}
            name={'branches'}
            defaultValue={initialBranchIds}
            render={({ field }) => {
              const ids: number[] = field.value ?? [];
              const selected = ids
                .map((id) => allLocations.find((l) => l.locationId === id))
                .filter(Boolean);
              return (
                <div className="mb-1">
                  <Label className="d-block">
                    {intl.formatMessage({ id: 'customers.branches' })}
                  </Label>
                  <Button.Ripple
                    type="button"
                    color="primary"
                    outline
                    size="sm"
                    onClick={() => setIsBranchesPickerOpen(true)}
                  >
                    {intl.formatMessage({ id: 'customers.selectBranches' })}
                  </Button.Ripple>
                  <Button.Ripple
                    type="button"
                    color="primary"
                    outline
                    size="sm"
                    className="ml-1"
                    onClick={() => setIsCreateBranchOpen(true)}
                  >
                    {intl.formatMessage({ id: 'customers.newBranch' })}
                  </Button.Ripple>
                  {selected.length > 0 && (
                    <div className="mt-1 d-flex flex-wrap" style={{ gap: '0.25rem' }}>
                      {selected.map((loc: any) => (
                        <Badge
                          key={loc.locationId}
                          color="light-primary"
                          className="d-inline-flex align-items-center"
                          style={{ padding: '0.4rem 0.6rem' }}
                        >
                          <span className="mr-50">{loc.locationName}</span>
                          <X
                            size={14}
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              field.onChange(ids.filter((id) => id !== loc.locationId))
                            }
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            }}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name={'mainOfficeId'}
            defaultValue={initialValues?.mainOfficeId}
            render={({ field, fieldState }) => (
              <LocationsDropdownInput
                field={field}
                fieldstate={fieldState}
                isInModal
                isClearable
                id={'mainOfficeInput'}
                options={mainOfficeOptions}
                label={intl.formatMessage({ id: 'customers.centralOffice' })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="customerName"
            defaultValue={initialValues?.customerName}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'customerNameInput'}
                label={intl.formatMessage({
                  id: 'customers.customerName'
                })}
                required
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="distributionChannel"
            defaultValue={initialValues?.distributionChannel}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'distributionChannelInput'}
                label={intl.formatMessage({
                  id: 'customers.distributionChannel'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="businessName"
            defaultValue={initialValues?.businessName}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'businessNameInput'}
                label={intl.formatMessage({
                  id: 'customers.businessName'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="taxId"
            defaultValue={initialValues?.taxId}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'taxIdInput'}
                label={intl.formatMessage({
                  id: 'customers.taxesId'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="privateCode"
            defaultValue={initialValues?.privateCode}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'privateCodeInput'}
                label={intl.formatMessage({
                  id: 'customers.privateCode'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="customerAddress"
            defaultValue={initialValues?.customerAddress}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'customerAddressInput'}
                label={intl.formatMessage({
                  id: 'customers.address'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="customerEmails"
            defaultValue={initialValues?.customerEmails}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'customerEmailsInput'}
                label={intl.formatMessage({
                  id: 'customers.email'
                })}
                type={'email'}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="contactName1"
            defaultValue={initialValues?.contactName1}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'contactName1Input'}
                label={intl.formatMessage({
                  id: 'customers.contact1'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="phone1"
            defaultValue={initialValues?.phone1}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'phone1Input'}
                label={intl.formatMessage({
                  id: 'customers.phone1'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="contactName2"
            defaultValue={initialValues?.contactName2}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'contactName2Input'}
                label={intl.formatMessage({
                  id: 'customers.contact2'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="phone2"
            defaultValue={initialValues?.phone2}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'phone2Input'}
                label={intl.formatMessage({
                  id: 'customers.phone2'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="contactName3"
            defaultValue={initialValues?.contactName3}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'contactName3Input'}
                label={intl.formatMessage({
                  id: 'customers.contact3'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="phone3"
            defaultValue={initialValues?.phone3}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'phone3Input'}
                label={intl.formatMessage({
                  id: 'customers.phone3'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="comments"
            defaultValue={initialValues?.comments}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'commentsInput'}
                label={intl.formatMessage({
                  id: 'customers.comments'
                })}
                type={'textarea'}
              />
            )}
          />
        </Col>
      </Row>

      <br />
      <br />
      <Row className={'footer-slide-pane'}>
        <Col sm="12">
          <Button.Ripple
            className="mr-1 mt-50 mb-50"
            id="saveCustomersButton"
            color="primary"
            /*type="submit"*/
            onClick={handleSubmit((values) =>
              isNew ? createCustomer(values) : editCustomer(values)
            )}
          >
            <FormattedMessage id="common.save" />
          </Button.Ripple>
          <Button.Ripple
            className="mt-50 mb-50"
            color="light"
            onClick={() => {
              closeSlidingPaneAddEditCustomer();
              reset();
            }}
          >
            <FormattedMessage id="common.cancel" />
          </Button.Ripple>
        </Col>
      </Row>
      {isBranchesPickerOpen && (
        <SlidingPaneSelectBranchesToLink
          isOpenSlidingPane={isBranchesPickerOpen}
          excludeCustomerId={customerId ?? 0}
          membership={{
            operatorId: formValues?.membership?.operatorId,
            organizationId: formValues?.membership?.organizationId,
            divisionId: formValues?.membership?.divisionId,
            subdivisionId: formValues?.membership?.subdivisionId
          }}
          closeSlidingPane={(selectedLocations) => {
            setIsBranchesPickerOpen(false);
            if (selectedLocations && selectedLocations.length > 0) {
              const ids = selectedLocations.map((loc) => loc.locationId);
              const merged = Array.from(new Set([...selectedBranchIds, ...ids]));
              setValue('branches', merged, { shouldDirty: true });
            }
          }}
        />
      )}
      {isCreateBranchOpen && (
        <SlidingPaneEditLocation
          title={intl.formatMessage({ id: 'customers.addBranch' })}
          isOpenSlidingPane={isCreateBranchOpen}
          closeSlidingPane={() => setIsCreateBranchOpen(false)}
          editingLocation={undefined}
          isCustomerLocation={true}
          selectedCustomerId={customerId}
          customerRequired={!!customerId}
          action={(values) => {
            locationIdsBeforeAddRef.current = new Set(
              allLocations.map((l) => l.locationId)
            );
            const basePayload = {
              ...values,
              ...values.membership,
              distributionChannel: values.distributionChannel,
              enableDispatch: 0,
              dispatchNotificationFreq: 0,
              dispatchEmails: null,
              dispatchCompletionType: 0
            };
            if (customerId) {
              dispatch(
                locationActions.startAddingLocation({
                  ...basePayload,
                  customerId,
                  locationTypeId: 1,
                  locationClassId: 34
                })
              );
            } else {
              dispatch(
                locationActions.startAddingLocation({
                  ...basePayload,
                  customerId: null,
                  locationTypeId: 2,
                  locationClassId: 0,
                  __messageObject: 'customers.branch'
                })
              );
            }
            setIsCreateBranchOpen(false);
          }}
        />
      )}
    </SlidingPane>
  );
};

SlidingPaneAddEditCustomer.defaultProps = {};

export default SlidingPaneAddEditCustomer;
